import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { isOrgOwner } from '@/utils/moduleAccess';
import { setWorkspaceModule } from '@/store/authSlice';

/**
 * Active application for sidebar and app launcher.
 * Org owners: workspaceModule (switch without re-login). Scoped users: JWT entryModule.
 */
export function useActiveModule(): ErpModule | null {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const workspaceModule = useAppSelector((s) => s.auth.workspaceModule);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const owner = isOrgOwner(permissions, roleCode);

  const inferred = useMemo(
    () => resolveModuleFromPath(location.pathname),
    [location.pathname],
  );

  const fallback = useMemo(() => {
    for (const code of enabledModules) {
      if (getModuleEntryByErpModule(code)) return code as ErpModule;
    }
    return 'POS' as ErpModule;
  }, [enabledModules]);

  const activeModule = owner
    ? (workspaceModule ?? inferred ?? fallback)
    : (entryModule ?? workspaceModule ?? inferred ?? fallback);

  useEffect(() => {
    if (!owner || workspaceModule) return;
    if (isPlatformAdminRoute(location.pathname)) return;
    const resolved = inferred ?? fallback;
    if (resolved) {
      dispatch(setWorkspaceModule(resolved));
    }
  }, [owner, workspaceModule, inferred, fallback, dispatch, location.pathname]);

  return activeModule;
}
